import './te-linear-loader';
import { customElement, property } from 'lit/decorators.js';
import { StyleInfo, styleMap } from 'lit-html/directives/style-map';
import { html, css } from 'lit';
import { TeComponent } from './common/te-component';
import { theme } from './common/theme';
import { IconName } from './te-icon';
import './te-text';
import './te-spacing';
import './button/te-button-squared';
import { booleanConverter } from './common/converters';
import { pathByName } from './icons/icon.utils';

/** Notification Component */
@customElement('te-notification')
export class TeNotification extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-notification-wrapper {
        display: flex;
        min-width: 304px;
        width: fit-content;
        border-radius: 4px;
        box-shadow: ${theme.shadows.secondary};
        box-sizing: border-box;
        opacity: 0;
      }

      .open {
        pointer-events: auto;
        opacity: 1;
      }

      .te-notification-status-bar {
        width: 4px;
        border-radius: 4px 0px 0px 4px;
      }

      .te-notification-content-wrapper {
        display: flex;
        flex-direction: column;
        width: 256px;
        margin-left: 20px;
        margin-top: 12px;
        margin-bottom: 12px;
        gap: 8px;
      }

      .te-notification-heading {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .button-wrapper {
        align-self: center;
        margin-right: 12px;
      }

      te-text {
        letter-spacing: 0px;
      }
    `,
  ];

  /** Title text for notification */
  @property({ type: String }) heading = '';
  /** Primary text value */
  @property({ type: String }) text = '';
  /** Defines if notification is visible or hidden */
  @property({ type: Boolean, converter: booleanConverter }) open = true;
  /** Background color of notification */
  @property({ type: String }) backgroundColor = theme.colors.shade0.toString();
  /** Background color of notification's status bar */
  @property({ type: String }) statusBarColor?: string;
  /** Pre-styled notification variant */
  @property({ type: String }) variant: 'default' | 'success' | 'warning' | 'error' = 'default';
  /** Defines if notification has a close button */
  @property({ type: Boolean, converter: booleanConverter }) hasCloseButton = false;
  /** Background color of button */
  @property({ type: String }) closeButtonBackgroundColor?: string;
  /** Icon name from icon pack */
  @property({ type: String }) icon?: IconName;
  /** Color of icon */
  @property({ type: String }) iconColor?: string;
  /** Size of icon */
  @property({ type: Number }) iconSize?: number;
  /** Defines if notification has a loader */
  @property({ type: Boolean, converter: booleanConverter }) hasLoader = false;
  /** Progress value between 0 and 100 */
  @property({ type: Number }) progress?: number;

  onNotificationClose(e: Event) {
    e.preventDefault();
    this.sendCustomEvent('teClose', this);
  }

  renderLinearLoader() {
    if (!this.hasLoader) return;
    return html`
      <te-linear-loader .progress=${this.progress}></te-linear-loader>
    `;
  }

  isValidIcon(iconName: string): iconName is IconName {
    return pathByName.hasOwnProperty(iconName);
  }

  renderIcon() {
    const { icon, iconSize = 14, iconColor = theme.colors.shade5.toString() } = this;
    if (!icon || !this.isValidIcon(icon)) return;
    return html`
      <te-icon name=${icon} color=${iconColor} size=${iconSize}></te-icon>
    `;
  }

  renderCloseButton() {
    if (!this.hasCloseButton) return;
    return html`
      <div class="button-wrapper">
        <te-button-squared
          backgroundColor=${this.closeButtonBackgroundColor || theme.colors.shade1.toString()}
          icon="close"
          @teClick=${this.onNotificationClose}
        ></te-button-squared>
      </div>
    `;
  }

  getStatusBackgroundColor() {
    return (
      {
        default: this.statusBarColor || theme.colors.primary.v0.toString(),
        success: theme.colors.available.toString(),
        warning: theme.colors.warn.toString(),
        error: theme.colors.inputError.toString(),
      }[this.variant] ||
      this.statusBarColor ||
      theme.colors.primary.v0.toString()
    );
  }

  getWrapperClass() {
    const classes = ['te-notification-wrapper', ...(this.open ? ['open'] : [])];
    return classes.join(' ');
  }

  getWrapperStyle() {
    return {
      backgroundColor: this.backgroundColor,
    };
  }

  getContentWrapperStyle(): StyleInfo {
    return {
      marginRight:
        this.hasCloseButton && this.hasLoader ? '16px' : this.hasCloseButton ? '12px' : '24px',
    };
  }

  getStatusBarStyle(): StyleInfo {
    return {
      backgroundColor: this.getStatusBackgroundColor(),
    };
  }

  render() {
    return html`
      <div class=${this.getWrapperClass()} style=${styleMap(this.getWrapperStyle())}>
        <div class="te-notification-status-bar" style=${styleMap(this.getStatusBarStyle())}></div>
        <div
          class="te-notification-content-wrapper"
          style=${styleMap(this.getContentWrapperStyle())}
        >
          <div class="te-notification-heading">
            ${this.renderIcon()}
            <te-text size="16" weight="700" class="te-notification-heading">
              ${this.heading}
            </te-text>
          </div>
          <te-text weight="400" truncate lineClamp="2">${this.text}</te-text>
          ${this.renderLinearLoader()}
        </div>
        ${this.renderCloseButton()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-notification': TeNotification;
  }
}
